/**
 * Drafts Tab - Manage Plugin Drafts (Ant Design v5)
 * 
 * TASK-F1.7: Complete draft management UI ✅
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  Tag,
  Avatar,
  Row,
  Col,
  Card,
  Divider,
  Dropdown,
  Modal,
  Alert,
  Skeleton,
  Progress,
  Typography,
  Space,
  message
} from 'antd';
import {
  EditOutlined,
  RocketOutlined,
  DeleteOutlined,
  CopyOutlined,
  DownloadOutlined,
  MoreOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { navigateToTab } from '../utils/navigation';
import { apiService } from '../services/apiService';
import PublishDialog from '../components/PublishDialog';
import { clearManifestsCache } from '../../../plugins/registry';

const { Title, Text, Paragraph } = Typography;

interface PluginDraft {
  draft_id: string;
  user_id: string;
  plugin_id: string;
  name: string;
  description: string;
  status: string;
  files: Array<{
    path: string;
    content: string;
    size: number;
  }>;
  manifest?: any;
  created_at: string;
  updated_at: string;
  metadata: Record<string, any>;
}

const DraftsTab: React.FC = () => {
  const [drafts, setDrafts] = useState<PluginDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<PluginDraft | null>(null);
  const [draftToDeploy, setDraftToDeploy] = useState<PluginDraft | null>(null);
  const [draftToPublish, setDraftToPublish] = useState<PluginDraft | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [manifestDialogOpen, setManifestDialogOpen] = useState(false);
  const [draftToView, setDraftToView] = useState<PluginDraft | null>(null);

  // Fetch drafts
  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setLoading(true);

    try {
      const draftsData = await apiService.listDrafts();
      console.log('Drafts loaded:', draftsData);
      setDrafts(draftsData || []); // Handle null/undefined response
    } catch (error: any) {
      console.error('Error fetching drafts:', error);
      
      // Handle 401/403 authentication errors gracefully
      if (error?.message?.includes('401') || error?.message?.includes('403') || 
          error?.message?.includes('Unauthorized') || error?.message?.includes('Forbidden')) {
        message.warning({
          content: 'Authentication required. Please refresh the page or log in again.',
          duration: 8,
          key: 'drafts-error'
        });
        setDrafts([]); // Show empty state instead of redirecting
        setLoading(false);
        return; // Don't let the error propagate to cause redirect
      }
      
      const errorMsg = error?.message || error?.detail || 'Failed to load drafts';
      message.error({
        content: errorMsg,
        duration: 5,
        key: 'drafts-error' // Prevent duplicate error messages
      });
      setDrafts([]); // Set empty array to prevent reload loop
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'draft':
        return { color: 'default', label: '📝 Draft' };
      case 'testing':
        return { color: 'warning', label: '🧪 Testing' };
      case 'ready':
        return { color: 'success', label: '✅ Ready' };
      default:
        return { color: 'default', label: status };
    }
  };

  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  // Helper to get version from manifest or metadata
  const getVersion = (draft: PluginDraft | null): string => {
    if (!draft) return '0.1.0';
    return draft.manifest?.version || draft.metadata?.version || '0.1.0';
  };

  // Helper to get icon from manifest or metadata
  const getIcon = (draft: PluginDraft | null): string => {
    if (!draft) return '🔌';
    return draft.manifest?.icon || draft.metadata?.icon || '🔌';
  };

  const handleViewManifest = (draft: PluginDraft) => {
    setDraftToView(draft);
    setManifestDialogOpen(true);
  };

  const handlePublishDraft = (draft: PluginDraft) => {
    setDraftToPublish(draft);
    setPublishDialogOpen(true);
  };

  const handlePublishSuccess = () => {
    // Clear manifest cache so dashboard shows new plugin
    clearManifestsCache();
    
    // Refresh drafts list
    fetchDrafts();
    message.success('Plugin published successfully!');
  };

  const handleContinueEdit = (draft: PluginDraft) => {
    // Navigate to Create tab with draft ID
    navigateToTab('create');
    // TODO: Pass draft ID to Create tab for editing
    // For now, just navigate to create tab
  };

  const handleDeleteClick = (draft: PluginDraft) => {
    setDraftToDelete(draft);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!draftToDelete) return;

    try {
      await apiService.deleteDraft(draftToDelete.draft_id);
      setDrafts(prev => prev.filter(d => d.draft_id !== draftToDelete.draft_id));
      setDeleteDialogOpen(false);
      setDraftToDelete(null);
      message.success('Draft deleted successfully');
    } catch (error) {
      console.error('Error deleting draft:', error);
      message.error('Failed to delete draft');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDraftToDelete(null);
  };

  const handleDeployClick = (draft: PluginDraft) => {
    setDraftToDeploy(draft);
    setDeployDialogOpen(true);
  };

  const handleDeployConfirm = async () => {
    if (!draftToDeploy) return;
    
    setDeploying(true);
    
    // TODO: Call API to deploy draft
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    // Remove from drafts after successful deploy
    setDrafts(prev => prev.filter(d => d.draft_id !== draftToDeploy.draft_id));
    
    setDeploying(false);
    setDeployDialogOpen(false);
    setDraftToDeploy(null);
    message.success('Plugin deployed successfully!');
  };

  const handleDeployCancel = () => {
    setDeployDialogOpen(false);
    setDraftToDeploy(null);
  };

  const getMenuItems = (draft: PluginDraft) => [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Continue Editing',
      onClick: () => handleContinueEdit(draft)
    },
    {
      key: 'view-manifest',
      icon: <FileTextOutlined />,
      label: 'View Manifest',
      onClick: () => handleViewManifest(draft)
    },
    ...(draft.status === 'built' ? [{
      key: 'publish',
      icon: <RocketOutlined />,
      label: 'Publish Plugin',
      onClick: () => handlePublishDraft(draft)
    }] : []),
    {
      key: 'duplicate',
      icon: <CopyOutlined />,
      label: 'Duplicate Draft'
    },
    {
      key: 'export',
      icon: <DownloadOutlined />,
      label: 'Export Code'
    },
    {
      type: 'divider' as const
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete Draft',
      danger: true,
      onClick: () => handleDeleteClick(draft)
    }
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <Title level={3}>Drafts</Title>
        <Text type="secondary">
          Continue working on your AI-generated apps or deploy them to production
        </Text>
      </div>

      {/* Info Card */}
      {drafts.length > 0 && (
        <Alert
          message={
            <div>
              <Text strong>Tip:</Text> Drafts are automatically saved as you work with the AI plugin builder. 
              Once ready, you can deploy them as production plugins.
            </div>
          }
          type="info"
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Drafts List */}
      {loading ? (
        <Row gutter={[16, 16]}>
          {[1, 2, 3].map((i) => (
            <Col xs={24} md={12} lg={8} key={i}>
              <Card>
                <Skeleton active />
              </Card>
            </Col>
          ))}
        </Row>
      ) : drafts.length === 0 ? (
        <Card>
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <Title level={5} type="secondary">
              📝 No drafts yet
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              Start building a custom plugin with AI and your drafts will appear here.
            </Text>
            <Button
              type="primary"
              size="large"
              icon={<span>✨</span>}
              onClick={() => navigateToTab('create')}
            >
              Create Plugin with AI
            </Button>
          </div>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {drafts.map((draft) => (
            <Col xs={24} md={12} lg={8} key={draft.draft_id}>
              <Card
                hoverable
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                {/* Draft Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
                  <Avatar
                    size={56}
                    style={{ fontSize: '2rem', backgroundColor: '#fff7e6' }}
                  >
                    {getIcon(draft)}
                  </Avatar>
                  <div style={{ flex: 1 }}>
                    <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                      {draft.name}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      v{getVersion(draft)}
                    </Text>
                  </div>
                  <Dropdown
                    menu={{ items: getMenuItems(draft) }}
                    trigger={['click']}
                  >
                    <Button type="text" icon={<MoreOutlined />} size="small" />
                  </Dropdown>
                </div>

                {/* Status Badge */}
                <div style={{ marginBottom: 16 }}>
                  <Tag color={getStatusConfig(draft.status).color}>
                    {getStatusConfig(draft.status).label}
                  </Tag>
                </div>

                {/* Description */}
                <Paragraph type="secondary" style={{ marginBottom: 16, flex: 1 }}>
                  {draft.description}
                </Paragraph>

                <Divider style={{ margin: '16px 0' }} />

                {/* Metadata */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    📅 Created {getTimeAgo(draft.created_at)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    🔄 Updated {getTimeAgo(draft.updated_at)}
                  </Text>
                  {draft.metadata?.source && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      📦 Source: {draft.metadata.source}
                    </Text>
                  )}
                </div>

                {/* Actions */}
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Space style={{ width: '100%' }}>
                    <Button
                      block
                      icon={<FileTextOutlined />}
                      onClick={() => handleViewManifest(draft)}
                      style={{ flex: 1 }}
                    >
                      View Manifest
                    </Button>
                    {draft.status === 'built' && (
                      <Button
                        type="primary"
                        block
                        icon={<RocketOutlined />}
                        onClick={() => handlePublishDraft(draft)}
                        style={{ flex: 1 }}
                      >
                        Publish
                      </Button>
                    )}
                    {draft.status !== 'built' && (
                      <Button
                        block
                        icon={<EditOutlined />}
                        onClick={() => handleContinueEdit(draft)}
                        style={{ flex: 1 }}
                      >
                        Continue
                      </Button>
                    )}
                  </Space>
                  <Button
                    danger
                    block
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteClick(draft)}
                    style={{ marginTop: 8 }}
                  >
                    Delete Draft
                  </Button>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Delete Confirmation Dialog */}
      <Modal
        title={
          <Space>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <span>Delete Draft?</span>
          </Space>
        }
        open={deleteDialogOpen}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        okButtonProps={{ danger: true, icon: <DeleteOutlined /> }}
      >
        <Alert
          message="This action cannot be undone!"
          type="warning"
          style={{ marginBottom: 16 }}
        />
        <Paragraph>
          Are you sure you want to delete <Text strong>{draftToDelete?.name}</Text> (draft ID: {draftToDelete?.draft_id})?
        </Paragraph>
        <Paragraph type="secondary">
          All draft code, configurations, and AI session history will be permanently removed.
        </Paragraph>
      </Modal>

      {/* Publish Dialog */}
      <PublishDialog
        visible={publishDialogOpen}
        draft={draftToPublish}
        onClose={() => {
          setPublishDialogOpen(false);
          setDraftToPublish(null);
        }}
        onSuccess={handlePublishSuccess}
      />

      {/* Deploy Confirmation Dialog */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar style={{ fontSize: '1.5rem', backgroundColor: '#f6ffed' }}>
              {getIcon(draftToDeploy)}
            </Avatar>
            <div>
              <div>Deploy Plugin?</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {draftToDeploy?.name} v{getVersion(draftToDeploy)}
              </Text>
            </div>
          </div>
        }
        open={deployDialogOpen}
        onOk={handleDeployConfirm}
        onCancel={handleDeployCancel}
        okText={deploying ? 'Deploying...' : 'Deploy'}
        okButtonProps={{ 
          loading: deploying, 
          icon: <RocketOutlined />,
          type: 'primary'
        }}
        width={600}
      >
        <Alert
          message="This draft is ready for deployment!"
          type="success"
          style={{ marginBottom: 16 }}
        />
        <div>
          <Paragraph>
            <Text strong>{draftToDeploy?.name}</Text> (draft ID: {draftToDeploy?.draft_id}) will be:
          </Paragraph>
          <ul style={{ marginTop: 8, marginBottom: 16 }}>
            <li><Text>Validated and built</Text></li>
            <li><Text>Added to your installed plugins</Text></li>
            <li><Text>Enabled immediately</Text></li>
            <li><Text>Removed from drafts</Text></li>
          </ul>
          {deploying && (
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Deploying plugin...
              </Text>
              <Progress percent={100} status="active" />
            </div>
          )}
        </div>
      </Modal>

      {/* Manifest View Dialog */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FileTextOutlined style={{ fontSize: '1.5rem' }} />
            <div>
              <div>Plugin Manifest</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {draftToView?.name} v{getVersion(draftToView)}
              </Text>
            </div>
          </div>
        }
        open={manifestDialogOpen}
        onCancel={() => {
          setManifestDialogOpen(false);
          setDraftToView(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setManifestDialogOpen(false);
              setDraftToView(null);
            }}
          >
            Close
          </Button>,
          draftToView?.status === 'built' && (
            <Button
              key="publish"
              type="primary"
              icon={<RocketOutlined />}
              onClick={() => {
                setManifestDialogOpen(false);
                handlePublishDraft(draftToView);
                setDraftToView(null);
              }}
            >
              Publish Plugin
            </Button>
          )
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {draftToView && (
          <div>
            <pre style={{
              background: '#1e1e1e',
              color: '#d4d4d4',
              padding: '16px',
              borderRadius: '8px',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: '13px',
              lineHeight: 1.5,
              overflow: 'auto',
              maxHeight: '500px',
              margin: 0,
              whiteSpace: 'pre-wrap'
            }}>
              {JSON.stringify(draftToView.manifest || draftToView.metadata, null, 2)}
            </pre>

            {draftToView.files && draftToView.files.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Files ({draftToView.files.length}):</Text>
                <div style={{
                  marginTop: 8,
                  padding: '12px',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  {draftToView.files.map((file, index) => (
                    <div key={index} style={{ marginBottom: 4, fontSize: 12 }}>
                      <Text code>{file.path}</Text>
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        ({file.size} bytes)
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DraftsTab;

